import React, { useEffect, useMemo } from 'react'

import { ApolloProvider } from '@apollo/client'
import classNames from 'classnames'

import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'
import { t } from 'src/translations'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { PopoverContextProvider } from 'shared/components/common/popover/PopoverContext'

import PageWrapper from 'public/components/default_template/PageWrapper'
import { CART_POPOVER_CONTEXT_KEY } from 'public/components/default_template/online_ordering/cart/CartModal'
import { getCheckoutEligibleItems } from 'public/components/default_template/online_ordering/cart/cartUtils'
import SideBySideCheckoutForm from 'public/components/default_template/online_ordering/checkout/side_by_side/CheckoutForm'
import {
  CartContextProvider,
  useCart
} from 'public/components/online_ordering/CartContext'
import {
  CheckoutContextProvider,
  useCheckout
} from 'public/components/online_ordering/CheckoutContext'
import { DeliveryContextProvider } from 'public/components/online_ordering/DeliveryContext'
import { FulfillmentContextProvider } from 'public/components/online_ordering/FulfillmentContext'
import { GiftCardContextProvider } from 'public/components/online_ordering/GiftCardContext'
import {
  LoyaltyContextProvider,
  useLoyalty
} from 'public/components/online_ordering/LoyaltyContext'
import { MarketingContextProvider } from 'public/components/online_ordering/MarketingContext'
import OffersContextProvider, {
  useOffers
} from 'public/components/online_ordering/OffersContext'
import { PaymentContextProvider } from 'public/components/online_ordering/PaymentContext'
import { TimeBasedRuleContextProvider } from 'public/components/online_ordering/TimeBasedRuleContext'
import { UpsellContextProvider } from 'public/components/online_ordering/UpsellContext'
import { getGlobalNavOverrideForOO } from 'public/components/online_ordering/globalNavUtils'
import { Channel } from 'public/js/siteUtilities'

import { DebugPanel } from './debug/DebugPanel'
import { getFirstOrThirdParty } from './online_ordering/checkout/loyalty/LoyaltyUtils'
import { A11yProvider } from '../online_ordering/useA11y'

export const EcommCheckoutPageContent = ({ nav }: { nav: React.ReactNode }) => {
  const { loadingCart, cart, error: cartError } = useCart()
  const isCartLoading = !cart && loadingCart
  const { track } = useThrottledTracker()
  const { pointsBalance, redemptions } = useLoyalty()
  const { ooRestaurant } = useRestaurant()
  const { enabledPaymentOptions } = useCheckout()
  const { achievedOffers } = useOffers()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  // display a white background on mobile, cart is empty, cart has an error, or while the cart is loading to prevent a white+grey background behind the loading skeleton
  const useWhiteBackground = useMemo(
    () =>
      isCartLoading ||
      !getCheckoutEligibleItems(cart)?.length ||
      isMobile ||
      !!cartError,
    [isCartLoading, cart, isMobile, cartError]
  )

  useEffect(() => {
    if (typeof window !== 'undefined' && ooRestaurant) {
      track(TrackingEvents.ECOMM_CHECKOUT_PAGE_VIEW, {
        firstOrThirdParty: getFirstOrThirdParty(
          ooRestaurant.loyaltyConfig?.programName
        ),
        pointsBalance: pointsBalance,
        numOfRedemptions: redemptions.length,
        numOffersAchieved: achievedOffers?.length ?? 0,
        enabledPaymentOptions: Array.from(enabledPaymentOptions.values())
      })
    }
    // run once when all the data has finished loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track, ooRestaurant, pointsBalance, redemptions.length])

  return (
    <div
      className={classNames(
        'defaultTemplate',
        useWhiteBackground ? 'whiteBackground' : 'whiteAndGrayBackground'
      )}
      data-testid='checkoutPage'
    >
      {nav}
      <div className={classNames('paddedSection', 'sideBySidePaddedSection')}>
        <div
          className='checkout'
          role='main'
          id='main'
          aria-label={t('ecomm-checkout-page.title')}
        >
          <SideBySideCheckoutForm />
          <DebugPanel />
        </div>
      </div>
    </div>
  )
}

const RestaurantCheckoutPage = () => {
  const { restaurant } = useRestaurant()
  const navOverride = getGlobalNavOverrideForOO(restaurant)
  return (
    <PageWrapper
      Component={EcommCheckoutPageContent}
      shouldShowPreviewBanner={false}
      navTypeOverride={navOverride}
    />
  )
}

export const ContextualEcommCheckoutPage = ({
  children
}: React.PropsWithChildren<{}>) => {
  const ooClient = useOOClient()

  return (
    <ApolloProvider client={ooClient}>
      <A11yProvider>
        <CartContextProvider channel={Channel.ECOMM}>
          <DeliveryContextProvider>
            <FulfillmentContextProvider>
              <TimeBasedRuleContextProvider>
                <GiftCardContextProvider>
                  <PaymentContextProvider>
                    <MarketingContextProvider>
                      <CheckoutContextProvider>
                        <LoyaltyContextProvider>
                          <OffersContextProvider>
                            <UpsellContextProvider>
                              <PopoverContextProvider
                                contextKey={CART_POPOVER_CONTEXT_KEY}
                              >
                                {children}
                              </PopoverContextProvider>
                            </UpsellContextProvider>
                          </OffersContextProvider>
                        </LoyaltyContextProvider>
                      </CheckoutContextProvider>
                    </MarketingContextProvider>
                  </PaymentContextProvider>
                </GiftCardContextProvider>
              </TimeBasedRuleContextProvider>
            </FulfillmentContextProvider>
          </DeliveryContextProvider>
        </CartContextProvider>
      </A11yProvider>
    </ApolloProvider>
  )
}

const EcommCheckoutPage = () => (
  <ContextualEcommCheckoutPage>
    <RestaurantCheckoutPage />
  </ContextualEcommCheckoutPage>
)
export default EcommCheckoutPage
